import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import SwipeNavigationGuard from "@/components/SwipeNavigationGuard";

import { AuthProvider } from "@/contexts/AuthContext";
import { LoginDialogProvider } from "@/contexts/LoginDialogContext";
import { SignupDialogProvider } from "@/contexts/SignupDialogContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { TripCartProvider } from "@/contexts/TripCartContext";
import LoginDialog from "@/components/auth/LoginDialog";
import SignupDialog from "@/components/auth/SignupDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import MobileNav from "@/components/MobileNav";
import GlobalHeaderHideOnScroll from "@/components/GlobalHeaderHideOnScroll";
import AppearanceLoader from "@/components/AppearanceLoader";
import E2EBootstrap from "@/components/E2EBootstrap";
import LocationBootstrap from "@/components/LocationBootstrap";
import PageVisibilityGuard from "@/components/PageVisibilityGuard";
import LandingNavbar from "@/components/landing/Navbar";
import { useLocation } from "react-router-dom";

// Defer non-critical chrome — these never block first paint.
const HelpChatBot = lazy(() => import("@/components/HelpChatBot"));
const MaintenanceOverlay = lazy(() => import("@/components/MaintenanceOverlay"));
const PushBootstrap = lazy(() => import("@/components/PushBootstrap"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const PwaInstallPrompt = lazy(() => import("@/components/PwaInstallPrompt"));
const ElectionTrackerSyncBootstrap = lazy(() => import("@/components/elections/ElectionTrackerSyncBootstrap"));

// Routes that intentionally render WITHOUT the shared top header
// (auth screens, full-bleed call/meeting/debate surfaces, in-room/scanner views).
const NAVBAR_EXCLUDE_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/meeting",
];

const GlobalNavbar = () => {
  const { pathname } = useLocation();
  // Event detail page (/events/:id) should still get the navbar; only deep event
  // sub-routes (ticket/scan/receipt) are excluded — but those pages already render
  // their own Navbar where needed, so we just exclude auth + full-bleed surfaces.
  const excluded = NAVBAR_EXCLUDE_PREFIXES.some((p) =>
    p.endsWith("/") ? pathname.startsWith(p) : pathname === p || pathname.startsWith(p + "/")
  );
  if (excluded) return null;
  return <LandingNavbar />;
};

// Preserve deep path when redirecting legacy /polltrack/* URLs to /elections/*
const LegacyPolltrackRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const target = pathname.replace(/^\/polltrack/, "/elections") + search + hash;
  return <Navigate to={target} replace />;
};
// UpdatePrompt removed per user request — no more "new version" toast.

// Eagerly loaded core pages (landing + auth = small, hot-path)
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import NotFound from "./pages/NotFound";
const Feed = lazy(() => import("./pages/Feed"));
const GloogumVideo = lazy(() => import("./pages/GloogumVideo"));
const GloogumLiveRoom = lazy(() => import("./pages/GloogumLiveRoom"));
const CreatorStudio = lazy(() => import("./pages/CreatorStudio"));

// Lazy loaded pages
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Chat = lazy(() => import("./pages/Chat"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const Admin = lazy(() => import("./pages/Admin"));
const ReloadlyTestHarness = lazy(() => import("./pages/ReloadlyTestHarness"));
const GoogleApiAdmin = lazy(() => import("./pages/admin/GoogleApiAdmin"));
const BrowserCompatReport = lazy(() => import("./pages/admin/BrowserCompatReport"));
const AdminFileManager = lazy(() => import("./pages/admin/FileManager"));
const Settings = lazy(() => import("./pages/Settings"));
const EncryptionSettings = lazy(() => import("./pages/EncryptionSettings"));
const Devices = lazy(() => import("./pages/Devices"));
const LinkDevice = lazy(() => import("./pages/LinkDevice"));
const Meeting = lazy(() => import("./pages/Meeting"));
const CallHistory = lazy(() => import("./pages/CallHistory"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Subscription = lazy(() => import("./pages/Subscription"));
const JobBoard = lazy(() => import("./pages/JobBoard"));
const JobListingDetail = lazy(() => import("./pages/JobListingDetail"));
const ContractBids = lazy(() => import("./pages/Jobs"));
const ContractBidDetail = lazy(() => import("./pages/JobDetail"));
const MyBids = lazy(() => import("./pages/MyBids"));
const Auctions = lazy(() => import("./pages/Auctions"));
const AuctionAnalytics = lazy(() => import("./pages/AuctionAnalytics"));
const JobInsightsPage = lazy(() => import("./pages/JobInsights"));

const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventTicket = lazy(() => import("./pages/EventTicket"));
const EventScanner = lazy(() => import("./pages/EventScanner"));
const EventReceipt = lazy(() => import("./pages/EventReceipt"));
const MyTickets = lazy(() => import("./pages/MyTickets"));
const Dating = lazy(() => import("./pages/Dating"));
const LegalDirectory = lazy(() => import("./pages/LegalDirectory"));
const LegalDetail = lazy(() => import("./pages/LegalDetail"));
const BusinessDirectory = lazy(() => import("./pages/BusinessDirectory"));
const BusinessDetail = lazy(() => import("./pages/BusinessDetail"));
const SkillTrades = lazy(() => import("./pages/SkillTrades"));
const SkillTradeDetail = lazy(() => import("./pages/SkillTradeDetail"));
const Advertise = lazy(() => import("./pages/Advertise"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const CareerApply = lazy(() => import("./pages/CareerApply"));
const Press = lazy(() => import("./pages/Press"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Contact = lazy(() => import("./pages/Contact"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Safety = lazy(() => import("./pages/Safety"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Groups = lazy(() => import("./pages/Groups"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const JoinGroup = lazy(() => import("./pages/JoinGroup"));
const Forum = lazy(() => import("./pages/Forum"));
const ForumTopic = lazy(() => import("./pages/ForumTopic"));
const MapBookmarks = lazy(() => import("./pages/MapBookmarks"));
const Engage = lazy(() => import("./pages/Engage"));
const Meetings = lazy(() => import("./pages/Meetings"));
const News = lazy(() => import("./pages/News"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Airtime = lazy(() => import("./pages/Airtime"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const TwoFactorSetup = lazy(() => import("./pages/TwoFactorSetup"));
const KYCVerification = lazy(() => import("./pages/KYCVerification"));
const BuyCrypto = lazy(() => import("./pages/BuyCrypto"));
const Logistics = lazy(() => import("./pages/Logistics"));
const LogisticsDetail = lazy(() => import("./pages/LogisticsDetail"));
const LogisticsTrip = lazy(() => import("./pages/LogisticsTrip"));
const Predictions = lazy(() => import("./pages/Predictions"));
const PredictionDetail = lazy(() => import("./pages/PredictionDetail"));
const CurrencyFlip = lazy(() => import("./pages/CurrencyFlip"));
const Trading = lazy(() => import("./pages/Trading"));
const Accounting = lazy(() => import("./pages/Accounting"));
const Raffles = lazy(() => import("./pages/Raffles"));
const RaffleDetail = lazy(() => import("./pages/RaffleDetail"));
const ResumeAI = lazy(() => import("./pages/ResumeAI"));
const Invitations = lazy(() => import("./pages/Invitations"));
const RSVP = lazy(() => import("./pages/RSVP"));
const Escrow = lazy(() => import("./pages/Escrow"));
const EscrowCreate = lazy(() => import("./pages/EscrowCreate"));
const EscrowDetail = lazy(() => import("./pages/EscrowDetail"));
const JoinEscrow = lazy(() => import("./pages/JoinEscrow"));
const HallOfFame = lazy(() => import("./pages/HallOfFame"));
const HallDetail = lazy(() => import("./pages/HallDetail"));
const VirtualMarket = lazy(() => import("./pages/VirtualMarket"));
const VirtualMarketAsset = lazy(() => import("./pages/VirtualMarketAsset"));
const VirtualMarketHowItWorks = lazy(() => import("./pages/VirtualMarketHowItWorks"));
const Campus = lazy(() => import("./pages/Campus"));
const CampusDetail = lazy(() => import("./pages/CampusDetail"));
const CampusWikiHome = lazy(() => import("./pages/CampusWiki"));
const CampusWikiArticle = lazy(() => import("./pages/CampusWikiArticle"));
const MealPlanner = lazy(() => import("./pages/MealPlanner"));
const MealPlannerDay = lazy(() => import("./pages/MealPlannerDay"));
const MealPlannerRecipe = lazy(() => import("./pages/MealPlannerRecipe"));
const MealPlannerShopping = lazy(() => import("./pages/MealPlannerShopping"));
const BibleWall = lazy(() => import("./pages/BibleWall"));
const FileSharing = lazy(() => import("./pages/FileSharing"));
const BankConnect = lazy(() => import("./pages/BankConnect"));
const BankConnectDetail = lazy(() => import("./pages/BankConnectDetail"));
const FoodCourt = lazy(() => import("./pages/FoodCourt"));
const FoodCourtDetail = lazy(() => import("./pages/FoodCourtDetail"));
const SalonSpa = lazy(() => import("./pages/SalonSpa"));
const SalonSpaDetail = lazy(() => import("./pages/SalonSpaDetail"));
const DigitalCards = lazy(() => import("./pages/DigitalCards"));
const CardView = lazy(() => import("./pages/CardView"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const QuizVerse = lazy(() => import("./pages/QuizVerse"));
const WorkSafety = lazy(() => import("./pages/WorkSafety"));
const QRGenerator = lazy(() => import("./pages/QRGenerator"));
const Freelance = lazy(() => import("./pages/Freelance"));
const FreelanceDetail = lazy(() => import("./pages/FreelanceDetail"));
const Talent = lazy(() => import("./pages/Talent"));
const TalentDetail = lazy(() => import("./pages/TalentDetail"));
const TalentOnboarding = lazy(() => import("./pages/TalentOnboarding"));
const UtilityPayments = lazy(() => import("./pages/UtilityPayments"));
const EsimStore = lazy(() => import("./pages/EsimStore"));
const InterviewGenerator = lazy(() => import("./pages/InterviewGenerator"));
const GivingHub = lazy(() => import("./pages/GivingHub"));
const StaySphere = lazy(() => import("./pages/StaySphere"));
const StaySphereDetail = lazy(() => import("./pages/StaySphereDetail"));
const StaySphereList = lazy(() => import("./pages/StaySphereList"));
const StaySphereBookings = lazy(() => import("./pages/StaySphereBookings"));
const StaySpherePromote = lazy(() => import("./pages/StaySpherePromote"));
const TravelCheckout = lazy(() => import("./pages/travel/TravelCheckout"));
const TravelTrips = lazy(() => import("./pages/travel/TravelTrips"));
const TravelAlerts = lazy(() => import("./pages/travel/TravelAlerts"));
const TravelDeals = lazy(() => import("./pages/travel/TravelDeals"));
const TravelBuilder = lazy(() => import("./pages/travel/TravelBuilder"));
const TravelGroups = lazy(() => import("./pages/travel/TravelGroups"));
const TravelGroupJoin = lazy(() => import("./pages/travel/TravelGroupJoin"));
const TravelSupport = lazy(() => import("./pages/travel/TravelSupport"));
const UserDashboard = lazy(() => import("./pages/Dashboard"));
const ElectionTracker = lazy(() => import("./pages/ElectionTracker"));
const ElectionTrackerUpload = lazy(() => import("./pages/ElectionTrackerUpload"));
const ElectionTrackerElection = lazy(() => import("./pages/ElectionTrackerElection"));
const ElectionTrackerAdmin = lazy(() => import("./pages/ElectionTrackerAdmin"));
const ElectionTrackerMap = lazy(() => import("./pages/ElectionTrackerMap"));
const ElectionTrackerPollingUnit = lazy(() => import("./pages/ElectionTrackerPollingUnit"));
const ElectionTrackerLeaderboard = lazy(() => import("./pages/ElectionTrackerLeaderboard"));
const ElectionRaffle = lazy(() => import("./pages/ElectionRaffle"));
const AccountTeam = lazy(() => import("./pages/AccountTeam"));
const AccountActivity = lazy(() => import("./pages/AccountActivity"));
const NotificationPreferences = lazy(() => import("./pages/NotificationPreferences"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,           // 1 min — avoids refetching the same data on every nav
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false, // major perf win: stop refetching on tab focus
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-6 h-6 animate-spin text-primary" />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* UpdatePrompt removed */}
        <BrowserRouter>
          <AuthProvider>
          <AppearanceLoader />
          <E2EBootstrap />
          <LocationBootstrap />
            <CurrencyProvider>
            <LoginDialogProvider>
            <SignupDialogProvider>
            <TripCartProvider>
              <ScrollToTop />
              <ScrollToTopButton />
              <SwipeNavigationGuard />

              <GlobalNavbar />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-canceled" element={<PaymentCanceled />} />
                <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/video" element={<ProtectedRoute><GloogumVideo /></ProtectedRoute>} />
                <Route path="/video/live/:streamId" element={<ProtectedRoute><GloogumLiveRoom /></ProtectedRoute>} />
                <Route path="/video/studio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />

                <Route path="/video/:videoId" element={<ProtectedRoute><GloogumVideo /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                <Route path="/settings/encryption" element={<ProtectedRoute><EncryptionSettings /></ProtectedRoute>} />
                <Route path="/settings/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
                <Route path="/settings/devices/link" element={<ProtectedRoute><LinkDevice /></ProtectedRoute>} />
                <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
                <Route path="/marketplace/:id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
                <Route path="/bookmarks" element={<ProtectedRoute><MapBookmarks /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                <Route path="/admin/reloadly-test" element={<ProtectedRoute><ReloadlyTestHarness /></ProtectedRoute>} />
                <Route path="/admin/google-api" element={<ProtectedRoute><GoogleApiAdmin /></ProtectedRoute>} />
                <Route path="/admin/compat" element={<ProtectedRoute><BrowserCompatReport /></ProtectedRoute>} />
                <Route path="/admin/files" element={<ProtectedRoute><AdminFileManager /></ProtectedRoute>} />
                <Route path="/jobs" element={<ProtectedRoute><JobBoard /></ProtectedRoute>} />
                <Route path="/jobs/:id" element={<ProtectedRoute><JobListingDetail /></ProtectedRoute>} />
                <Route path="/contract-bids" element={<ProtectedRoute><ContractBids /></ProtectedRoute>} />
                <Route path="/contract-bids/:id" element={<ProtectedRoute><ContractBidDetail /></ProtectedRoute>} />
                <Route path="/my-bids" element={<ProtectedRoute><MyBids /></ProtectedRoute>} />
                <Route path="/auctions" element={<Auctions />} />
               <Route path="/auctions/analytics" element={<ProtectedRoute><AuctionAnalytics /></ProtectedRoute>} />
                <Route path="/auctions/:id" element={<ProtectedRoute><ListingDetail /></ProtectedRoute>} />
                <Route path="/job-insights" element={<ProtectedRoute><JobInsightsPage /></ProtectedRoute>} />
                
                <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
                <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
                <Route path="/events/:id/ticket" element={<ProtectedRoute><EventTicket /></ProtectedRoute>} />
                <Route path="/events/:id/scan" element={<ProtectedRoute><EventScanner /></ProtectedRoute>} />
                <Route path="/events/:id/receipt" element={<ProtectedRoute><EventReceipt /></ProtectedRoute>} />
                <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
                <Route path="/dating" element={<ProtectedRoute><Dating /></ProtectedRoute>} />
                <Route path="/legal-directory" element={<ProtectedRoute><LegalDirectory /></ProtectedRoute>} />
                <Route path="/legal-directory/:id" element={<ProtectedRoute><LegalDetail /></ProtectedRoute>} />
                <Route path="/business-directory" element={<ProtectedRoute><BusinessDirectory /></ProtectedRoute>} />
                <Route path="/business-directory/:id" element={<ProtectedRoute><BusinessDetail /></ProtectedRoute>} />
                <Route path="/skill-trades" element={<ProtectedRoute><SkillTrades /></ProtectedRoute>} />
                <Route path="/skill-trades/:id" element={<ProtectedRoute><SkillTradeDetail /></ProtectedRoute>} />
                <Route path="/advertise" element={<ProtectedRoute><Advertise /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
                <Route path="/meeting/:roomName" element={<ProtectedRoute><Meeting /></ProtectedRoute>} />
                <Route path="/calls" element={<ProtectedRoute><CallHistory /></ProtectedRoute>} />
                <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
                <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
                <Route path="/groups/:id" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
                <Route path="/join/:code" element={<ProtectedRoute><JoinGroup /></ProtectedRoute>} />
                <Route path="/forum" element={<ProtectedRoute><Forum /></ProtectedRoute>} />
                <Route path="/forum/:id" element={<ProtectedRoute><ForumTopic /></ProtectedRoute>} />
                <Route path="/engage" element={<ProtectedRoute><Engage /></ProtectedRoute>} />
                <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
                <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
                <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                <Route path="/logistics" element={<ProtectedRoute><Logistics /></ProtectedRoute>} />
                <Route path="/logistics/trip/:id" element={<ProtectedRoute><LogisticsTrip /></ProtectedRoute>} />
                <Route path="/logistics/:id" element={<ProtectedRoute><LogisticsDetail /></ProtectedRoute>} />

                <Route path="/predictions" element={<ProtectedRoute><Predictions /></ProtectedRoute>} />
                <Route path="/predictions/:id" element={<ProtectedRoute><PredictionDetail /></ProtectedRoute>} />
                <Route path="/currency-flip" element={<ProtectedRoute><CurrencyFlip /></ProtectedRoute>} />
                <Route path="/trading" element={<ProtectedRoute><Trading /></ProtectedRoute>} />
                <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} />
                <Route path="/raffles" element={<ProtectedRoute><Raffles /></ProtectedRoute>} />
                <Route path="/raffles/:id" element={<ProtectedRoute><RaffleDetail /></ProtectedRoute>} />
                <Route path="/resume-ai" element={<ProtectedRoute><ResumeAI /></ProtectedRoute>} />
                <Route path="/invitations" element={<ProtectedRoute><Invitations /></ProtectedRoute>} />
                <Route path="/rsvp/:token" element={<RSVP />} />
                <Route path="/escrow" element={<ProtectedRoute><Escrow /></ProtectedRoute>} />
                <Route path="/escrow/create" element={<ProtectedRoute><EscrowCreate /></ProtectedRoute>} />
                <Route path="/escrow/:id" element={<ProtectedRoute><EscrowDetail /></ProtectedRoute>} />
                <Route path="/escrow/join" element={<ProtectedRoute><JoinEscrow /></ProtectedRoute>} />
                <Route path="/hall" element={<ProtectedRoute><HallOfFame /></ProtectedRoute>} />
                <Route path="/hall/:id" element={<ProtectedRoute><HallDetail /></ProtectedRoute>} />
                <Route path="/virtual-market" element={<ProtectedRoute><VirtualMarket /></ProtectedRoute>} />
                <Route path="/virtual-market/how-it-works" element={<VirtualMarketHowItWorks />} />
                <Route path="/virtual-market/:id" element={<ProtectedRoute><VirtualMarketAsset /></ProtectedRoute>} />
                <Route path="/sharerent" element={<Navigate to="/virtual-market" replace />} />
                <Route path="/sharerent/:id" element={<Navigate to="/virtual-market" replace />} />
                <Route path="/campus" element={<ProtectedRoute><Campus /></ProtectedRoute>} />
                <Route path="/campus/:id" element={<ProtectedRoute><CampusDetail /></ProtectedRoute>} />
                <Route path="/campus/wiki" element={<ProtectedRoute><CampusWikiHome /></ProtectedRoute>} />
                <Route path="/campus/wiki/:slug" element={<ProtectedRoute><CampusWikiArticle /></ProtectedRoute>} />
                <Route path="/meal-planner" element={<ProtectedRoute><MealPlanner /></ProtectedRoute>} />
                <Route path="/meal-planner/:planId/shopping" element={<ProtectedRoute><MealPlannerShopping /></ProtectedRoute>} />
                <Route path="/meal-planner/:planId/day/:day" element={<ProtectedRoute><MealPlannerDay /></ProtectedRoute>} />
                <Route path="/meal-planner/:planId/recipe/:recipeId" element={<ProtectedRoute><MealPlannerRecipe /></ProtectedRoute>} />
                {/* Removed: /campus-library, /scan-capture/:code (Books Library) */}
                <Route path="/bible-wall" element={<ProtectedRoute><BibleWall /></ProtectedRoute>} />
                <Route path="/file-sharing" element={<ProtectedRoute><FileSharing /></ProtectedRoute>} />
                <Route path="/bank-connect" element={<ProtectedRoute><BankConnect /></ProtectedRoute>} />
                <Route path="/bank-connect/:id" element={<ProtectedRoute><BankConnectDetail /></ProtectedRoute>} />
                <Route path="/food-court" element={<ProtectedRoute><FoodCourt /></ProtectedRoute>} />
                <Route path="/food-court/:id" element={<ProtectedRoute><FoodCourtDetail /></ProtectedRoute>} />
                <Route path="/beauty" element={<ProtectedRoute><SalonSpa /></ProtectedRoute>} />
                <Route path="/beauty/:id" element={<ProtectedRoute><SalonSpaDetail /></ProtectedRoute>} />
                <Route path="/digital-cards" element={<ProtectedRoute><DigitalCards /></ProtectedRoute>} />
                <Route path="/card/:slug" element={<CardView />} />
                <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
                <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
                <Route path="/quizverse" element={<ProtectedRoute><QuizVerse /></ProtectedRoute>} />
                <Route path="/work-safety" element={<ProtectedRoute><WorkSafety /></ProtectedRoute>} />
                <Route path="/qr-generator" element={<ProtectedRoute><QRGenerator /></ProtectedRoute>} />
                <Route path="/freelance" element={<ProtectedRoute><Freelance /></ProtectedRoute>} />
                <Route path="/freelance/:id" element={<ProtectedRoute><FreelanceDetail /></ProtectedRoute>} />
                <Route path="/talent" element={<Talent />} />
                <Route path="/talent/onboarding" element={<ProtectedRoute><TalentOnboarding /></ProtectedRoute>} />
                <Route path="/talent/:id" element={<TalentDetail />} />
                <Route path="/utility-payments" element={<ProtectedRoute><UtilityPayments /></ProtectedRoute>} />
                <Route path="/esim-store" element={<ProtectedRoute><EsimStore /></ProtectedRoute>} />
                <Route path="/airtime" element={<ProtectedRoute><Airtime /></ProtectedRoute>} />
                <Route path="/gift-cards" element={<ProtectedRoute><GiftCards /></ProtectedRoute>} />
                <Route path="/interview-generator" element={<ProtectedRoute><InterviewGenerator /></ProtectedRoute>} />
                <Route path="/giving" element={<GivingHub />} />
                <Route path="/staysphere" element={<ProtectedRoute><StaySphere /></ProtectedRoute>} />
                <Route path="/staysphere/:id" element={<ProtectedRoute><StaySphereDetail /></ProtectedRoute>} />
                <Route path="/staysphere/list" element={<ProtectedRoute><StaySphereList /></ProtectedRoute>} />
                <Route path="/staysphere/bookings" element={<ProtectedRoute><StaySphereBookings /></ProtectedRoute>} />
                <Route path="/staysphere/promote" element={<ProtectedRoute><StaySpherePromote /></ProtectedRoute>} />
                <Route path="/travel/checkout" element={<ProtectedRoute><TravelCheckout /></ProtectedRoute>} />
                <Route path="/travel/trips" element={<ProtectedRoute><TravelTrips /></ProtectedRoute>} />
                <Route path="/travel/trips/:ref" element={<ProtectedRoute><TravelTrips /></ProtectedRoute>} />
                <Route path="/travel/alerts" element={<ProtectedRoute><TravelAlerts /></ProtectedRoute>} />
                <Route path="/travel/deals" element={<ProtectedRoute><TravelDeals /></ProtectedRoute>} />
                <Route path="/travel/builder" element={<ProtectedRoute><TravelBuilder /></ProtectedRoute>} />
                <Route path="/travel/groups" element={<ProtectedRoute><TravelGroups /></ProtectedRoute>} />
                <Route path="/travel/groups/:id" element={<ProtectedRoute><TravelGroups /></ProtectedRoute>} />
                <Route path="/travel/groups/join/:code" element={<TravelGroupJoin />} />
                <Route path="/travel/support" element={<ProtectedRoute><TravelSupport /></ProtectedRoute>} />
                <Route path="/travel/support/:id" element={<ProtectedRoute><TravelSupport /></ProtectedRoute>} />
                <Route path="/elections" element={<ElectionTracker />} />
                <Route path="/elections/upload" element={<ElectionTrackerUpload />} />
                <Route path="/elections/election/:id" element={<ElectionTrackerElection />} />
                <Route path="/elections/admin" element={<ProtectedRoute><ElectionTrackerAdmin /></ProtectedRoute>} />
                <Route path="/elections/map" element={<ElectionTrackerMap />} />
                <Route path="/elections/map/:id" element={<ElectionTrackerMap />} />
                <Route path="/elections/unit/:electionId/:unitId" element={<ElectionTrackerPollingUnit />} />
                <Route path="/elections/leaderboard" element={<ElectionTrackerLeaderboard />} />
                <Route path="/elections/raffle" element={<ElectionRaffle />} />
                {/* Legacy /polltrack/* URLs — redirect to new /elections/* paths */}
                <Route path="/polltrack" element={<Navigate to="/elections" replace />} />
                <Route path="/polltrack/*" element={<LegacyPolltrackRedirect />} />
                <Route path="/2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
                <Route path="/kyc" element={<ProtectedRoute><KYCVerification /></ProtectedRoute>} />
                <Route path="/buy-crypto" element={<ProtectedRoute><BuyCrypto /></ProtectedRoute>} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/careers/:slug" element={<CareerApply />} />
                <Route path="/press" element={<Press />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogDetail />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/bug-report" element={<Contact />} />
                <Route path="/account/team" element={<ProtectedRoute><AccountTeam /></ProtectedRoute>} />
                <Route path="/account/activity" element={<ProtectedRoute><AccountActivity /></ProtectedRoute>} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/safety" element={<Safety />} />
                <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <MobileNav />
            <LoginDialog />
            <SignupDialog />
            <GlobalHeaderHideOnScroll />
            <PageVisibilityGuard />
            <Suspense fallback={null}>
              <HelpChatBot />
              <MaintenanceOverlay />
              <PushBootstrap />
              <CookieConsent />
              <PwaInstallPrompt />
              <ElectionTrackerSyncBootstrap />
            </Suspense>
            </TripCartProvider>
            </SignupDialogProvider>
            </LoginDialogProvider>
            </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;

